import React from 'react';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy - Energy Spectrum Solar"
        description="Privacy Policy for Energy Spectrum. Learn how we collect, use, and protect your personal information in accordance with Australian privacy laws."
        keywords="privacy policy, data protection, personal information, Energy Spectrum, NSW, Australia"
      />
      
      <div className="page-wrapper">
        <div className="container">
          <div className="content-section">
            <h1 className="page-title">Privacy Policy</h1>
            <p className="body-text" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
              Last Updated: January 2025
            </p>

            <div className="legal-content">
              <section className="legal-section">
                <h2 className="section-title">1. Introduction</h2>
                <p className="body-text">
                  Energy Spectrum (ABN pending) ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information 
                  in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">2. Information We Collect</h2>
                <p className="body-text">We may collect the following types of personal information:</p>
                <ul className="legal-list">
                  <li><strong>Contact Information:</strong> Name, email address, phone number, and postal address</li>
                  <li><strong>Property Information:</strong> Address, property type, roof specifications, and energy consumption data</li>
                  <li><strong>Technical Information:</strong> IP address, browser type, device information, and website usage data</li>
                  <li><strong>Financial Information:</strong> Payment details and billing information for our services</li>
                  <li><strong>Communication Records:</strong> Records of your communications with us, including emails and phone calls</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="section-title">3. How We Collect Information</h2>
                <p className="body-text">We collect personal information through:</p>
                <ul className="legal-list">
                  <li>Website contact forms and quote requests</li>
                  <li>Email and phone communications</li>
                  <li>On-site consultations and assessments</li>
                  <li>Service agreements and contracts</li>
                  <li>Cookies and similar tracking technologies on our website</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="section-title">4. How We Use Your Information</h2>
                <p className="body-text">We use your personal information to:</p>
                <ul className="legal-list">
                  <li>Provide solar installation, maintenance, and related services</li>
                  <li>Process your inquiries and respond to your requests</li>
                  <li>Prepare quotes and proposals for solar systems</li>
                  <li>Process payments and manage billing</li>
                  <li>Communicate with you about our services, updates, and promotions</li>
                  <li>Improve our services and customer experience</li>
                  <li>Comply with legal obligations and government incentive programs (including federal and NSW battery rebate schemes)</li>
                  <li>Maintain records for warranty and support purposes</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="section-title">5. Information Disclosure</h2>
                <p className="body-text">We may disclose your personal information to:</p>
                <ul className="legal-list">
                  <li><strong>Service Providers:</strong> Third-party contractors, installers, and service providers who assist us in delivering our services</li>
                  <li><strong>Government Agencies:</strong> Relevant authorities for compliance with solar installation regulations, rebate programs, and licensing requirements</li>
                  <li><strong>Financial Institutions:</strong> Banks and payment processors for transaction processing</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
                  <li><strong>Business Partners:</strong> Solar panel manufacturers and equipment suppliers for warranty and technical support</li>
                </ul>
                <p className="body-text">
                  We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">6. Data Security</h2>
                <p className="body-text">
                  We implement appropriate technical and organizational measures to protect your personal information 
                  against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="legal-list">
                  <li>Secure servers and encrypted data transmission</li>
                  <li>Access controls and authentication procedures</li>
                  <li>Regular security assessments and updates</li>
                  <li>Staff training on data protection and privacy</li>
                </ul>
                <p className="body-text">
                  However, no method of transmission over the internet or electronic storage is 100% secure, and we 
                  cannot guarantee absolute security.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">7. Your Rights</h2>
                <p className="body-text">Under Australian privacy law, you have the right to:</p>
                <ul className="legal-list">
                  <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal and contractual obligations)</li>
                  <li><strong>Complaint:</strong> Lodge a complaint if you believe we have breached the Australian Privacy Principles</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                </ul>
                <p className="body-text">
                  To exercise these rights, please contact us using the details provided below.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">8. Cookies and Tracking Technologies</h2>
                <p className="body-text">
                  Our website uses cookies and similar tracking technologies to enhance your browsing experience, 
                  analyze website traffic, and understand user preferences. You can control cookie settings through 
                  your browser, but disabling cookies may limit certain website functionality.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">9. Third-Party Links</h2>
                <p className="body-text">
                  Our website may contain links to third-party websites. We are not responsible for the privacy 
                  practices or content of these external sites. We encourage you to review the privacy policies 
                  of any third-party websites you visit.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">10. Data Retention</h2>
                <p className="body-text">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in 
                  this Privacy Policy, comply with legal obligations (including warranty and compliance requirements), 
                  resolve disputes, and enforce our agreements. Typically, we retain customer records for a minimum 
                  of 7 years in accordance with Australian business record-keeping requirements.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">11. Changes to This Policy</h2>
                <p className="body-text">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal 
                  requirements. We will notify you of any material changes by posting the updated policy on our 
                  website with a revised "Last Updated" date. Your continued use of our services after such changes 
                  constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="section-title">12. Contact Us</h2>
                <p className="body-text">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <div className="contact-details" style={{ marginTop: '1.5rem' }}>
                  <p className="body-text"><strong>Energy Spectrum</strong></p>
                  <p className="body-text">Leppington, NSW 2179, Australia</p>
                  <p className="body-text">Email: <a href="mailto:info@energyspectrum.com.au" className="link-primary">info@energyspectrum.com.au</a></p>
                  <p className="body-text">Phone: <a href="tel:0404844257" className="link-primary">0404 844 257</a></p>
                </div>
              </section>

              <section className="legal-section">
                <h2 className="section-title">13. Complaints</h2>
                <p className="body-text">
                  If you believe we have breached the Australian Privacy Principles, you may lodge a complaint with us. 
                  We will investigate your complaint and respond within a reasonable timeframe. If you are not satisfied 
                  with our response, you may contact the Office of the Australian Information Commissioner (OAIC):
                </p>
                <div className="contact-details" style={{ marginTop: '1rem' }}>
                  <p className="body-text">Website: <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="link-primary">www.oaic.gov.au</a></p>
                  <p className="body-text">Phone: 1300 363 992</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;